import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../services/api";
import { useConfirm } from "../../hooks/useConfirm";
import RefreshButton from "../RefreshButton/RefreshButton";
import "./Researches.css";

const STATUS_LABELS = {
  briefing: "Briefing",
  in_progress: "In Progress",
  review: "Review",
  completed: "Completed",
  archived: "Archived",
};

const STATUS_ICONS = {
  briefing: "📋",
  in_progress: "🔍",
  review: "⚠️",
  completed: "✅",
  archived: "📦",
};

const OUTPUT_FORMATS = [
  "detailed_report",
  "comparison_table",
  "pros_cons",
  "recommendation",
];

function hasError(item) {
  const versions = item.versions || [];
  if (versions.length === 0) return false;
  const latest = versions[versions.length - 1];
  return !!latest.error;
}

function getErrorMessage(item) {
  const versions = item.versions || [];
  if (versions.length === 0) return null;
  const latest = versions[versions.length - 1];
  return latest.error || null;
}

function StatusIndicator({ status, hasErr }) {
  if (status === "in_progress") {
    return (
      <span className="status-indicator working" title="Muninn is researching...">
        <span className="orbit-ring">
          <span className="orbit-dot" />
        </span>
        <span className="pulse-core" />
      </span>
    );
  }
  if (hasErr) {
    return <span className="status-indicator error" title="Research failed">!</span>;
  }
  return null;
}

function CollapsibleSection({ title, count, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`collapsible-section ${open ? "open" : ""}`}>
      <button className="collapsible-header" onClick={() => setOpen(!open)}>
        <span className="collapsible-chevron">{open ? "▼" : "▶"}</span>
        <span className="collapsible-title">{title}</span>
        {count != null && <span className="collapsible-count">{count}</span>}
      </button>
      {open && <div className="collapsible-body">{children}</div>}
    </div>
  );
}

function parseFindings(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw); } catch { return null; }
}

function Researches() {
  const [researches, setResearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("active");
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pollRef = useRef(null);
  const [confirm, confirmDialog] = useConfirm();

  const fetchResearches = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const data = await apiClient.get("/researches");
      setResearches(data.items || []);
    } catch (err) {
      console.error("Failed to fetch researches:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResearches();
  }, [fetchResearches]);

  // Auto-poll list when any research is in_progress
  useEffect(() => {
    const anyWorking = researches.some((r) => r.status === "in_progress");
    if (anyWorking && !selectedId) {
      pollRef.current = setInterval(() => fetchResearches(true), 10000);
    }
    return () => clearInterval(pollRef.current);
  }, [researches, selectedId, fetchResearches]);

  const fetchDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const data = await apiClient.get(`/researches/${id}`);
      setDetail(data.item || data);
    } catch (err) {
      console.error("Failed to fetch research detail:", err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  const handleDelete = async (id) => {
    if (!(await confirm("Delete this research?"))) return;
    try {
      await apiClient.delete(`/researches/${id}`);
      if (selectedId === id) { setSelectedId(null); setDetail(null); }
      fetchResearches();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const filtered = researches.filter((r) => {
    if (viewMode === "active") return !["completed", "archived"].includes(r.status);
    if (viewMode === "completed") return r.status === "completed";
    if (viewMode === "archived") return r.status === "archived";
    return true;
  });

  const sorted = [...filtered].sort((a, b) =>
    (b.createdAt || "").localeCompare(a.createdAt || "")
  );

  if (selectedId && detail && !detailLoading) {
    return (
      <ResearchDetail
        item={detail}
        onBack={() => { setSelectedId(null); setDetail(null); }}
        onRefresh={() => { fetchDetail(selectedId); fetchResearches(); }}
      />
    );
  }

  return (
    <div className="researches-page">
      {confirmDialog}
      <div className="researches-header">
        <h2>Researches</h2>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <RefreshButton onClick={() => fetchResearches(false)} title="Refresh researches" />
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + New Research
          </button>
        </div>
      </div>

      <div className="researches-filters">
        <button
          className={`btn-toggle ${viewMode === "active" ? "active" : ""}`}
          onClick={() => setViewMode("active")}
        >Active</button>
        <button
          className={`btn-toggle ${viewMode === "completed" ? "active" : ""}`}
          onClick={() => setViewMode(viewMode === "completed" ? "active" : "completed")}
        >
          Completed ({researches.filter((r) => r.status === "completed").length})
        </button>
        <button
          className={`btn-toggle ${viewMode === "archived" ? "active" : ""}`}
          onClick={() => setViewMode(viewMode === "archived" ? "active" : "archived")}
        >
          Archived ({researches.filter((r) => r.status === "archived").length})
        </button>
        <span className="researches-count">
          {sorted.length} research{sorted.length !== 1 ? "es" : ""}
        </span>
      </div>

      {showForm && (
        <ResearchForm
          onSave={() => { setShowForm(false); fetchResearches(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <div className="placeholder">Loading...</div>
      ) : sorted.length === 0 ? (
        <div className="placeholder">
          No researches yet. Ask Anna to start one, or create a brief here.
        </div>
      ) : (
        <div className="researches-list">
          {sorted.map((r) => {
            const err = hasError(r);
            return (
              <div
                key={r.id}
                className={`research-card ${r.status} ${r.completionFlag ? "has-results" : ""} ${err ? "has-error" : ""}`}
                onClick={() => setSelectedId(r.id)}
              >
                <div className="research-top">
                  <div className="research-title-row">
                    <StatusIndicator status={r.status} hasErr={err} />
                    <span className="research-title">{r.title}</span>
                  </div>
                  <div className="research-badges">
                    <span className={`research-badge status-${r.status} ${r.status === "in_progress" ? "badge-glow" : ""} ${err ? "badge-error" : ""}`}>
                      {STATUS_ICONS[r.status]} {STATUS_LABELS[r.status] || r.status}
                    </span>
                    {r.completionFlag && !err && (
                      <span className="research-badge status-completed badge-pulse">Results ready</span>
                    )}
                    {err && (
                      <span className="research-badge badge-error">Failed</span>
                    )}
                  </div>
                </div>
                {r.status === "in_progress" && (
                  <div className="progress-sweep" />
                )}
                {r.goal && <p className="research-goal">{r.goal}</p>}
                <div className="research-meta">
                  {r.currentVersion > 0 && (
                    <span className="research-version">v{r.currentVersion}</span>
                  )}
                  {r.iterations > 0 && (
                    <span className="research-iterations">{r.iterations} pass{r.iterations !== 1 ? "es" : ""}</span>
                  )}
                  <span className="research-date">
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ""}
                  </span>
                  <div className="research-actions" onClick={(e) => e.stopPropagation()}>
                    {r.status === "briefing" && (
                      <button className="btn-primary btn-xs" onClick={async () => {
                        await apiClient.post(`/researches/${r.id}/retry`);
                        fetchResearches();
                      }}>
                        Launch
                      </button>
                    )}
                    <button className="btn-danger btn-xs" onClick={() => handleDelete(r.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ResearchDetail({ item, onBack, onRefresh }) {
  const [archiving, setArchiving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const pollRef = useRef(null);

  // Auto-poll detail when in_progress
  useEffect(() => {
    if (item.status === "in_progress") {
      pollRef.current = setInterval(() => onRefresh(), 8000);
    }
    return () => clearInterval(pollRef.current);
  }, [item.status, onRefresh]);

  const handleArchive = async () => {
    try {
      setArchiving(true);
      await apiClient.put(`/researches/${item.id}`, { status: "archived" });
      onRefresh();
    } catch (err) {
      console.error("Archive failed:", err);
    } finally {
      setArchiving(false);
    }
  };

  const handleRetry = async () => {
    try {
      setRetrying(true);
      await apiClient.post(`/researches/${item.id}/retry`);
      onRefresh();
    } catch (err) {
      console.error("Retry failed:", err);
    } finally {
      setRetrying(false);
    }
  };

  const versions = item.versions || [];
  const latest = versions.length > 0 ? versions[versions.length - 1] : null;
  const err = hasError(item);
  const errorMsg = getErrorMessage(item);

  return (
    <div className="research-detail">
      <button className="btn-secondary btn-back" onClick={onBack}>← Back</button>

      <div className="detail-header">
        <StatusIndicator status={item.status} hasErr={err} />
        <h2>{item.title}</h2>
        <span className={`research-badge status-${item.status} ${item.status === "in_progress" ? "badge-glow" : ""} ${err ? "badge-error" : ""}`}>
          {STATUS_ICONS[item.status]} {STATUS_LABELS[item.status] || item.status}
        </span>
      </div>

      {err && (
        <div className="error-banner">
          <div className="error-banner-icon">⚠️</div>
          <div className="error-banner-content">
            <strong>Research failed</strong>
            <p>{errorMsg}</p>
          </div>
          <button
            className="btn-primary btn-sm"
            onClick={handleRetry}
            disabled={retrying}
          >
            {retrying ? "Retrying..." : "Retry"}
          </button>
        </div>
      )}

      {item.status === "briefing" && (
        <div className="briefing-banner">
          <div className="briefing-banner-icon">📋</div>
          <div className="briefing-banner-content">
            <strong>Ready to launch</strong>
            <p>This research brief is ready. Launch Muninn to start researching.</p>
          </div>
          <button
            className="btn-primary btn-sm"
            onClick={handleRetry}
            disabled={retrying}
          >
            {retrying ? "Launching..." : "🚀 Launch"}
          </button>
        </div>
      )}

      {item.status === "in_progress" && (
        <div className="working-banner">
          <div className="working-animation">
            <span className="working-orbit">
              <span className="working-dot" />
            </span>
            <span className="working-core" />
          </div>
          <div className="working-text">
            <strong>Muninn is researching...</strong>
            <p>Results will appear automatically. This may take a few minutes.</p>
          </div>
          <div className="progress-sweep" />
        </div>
      )}

      <div className="detail-brief">
        <h3>Brief</h3>
        <div className="brief-grid">
          {item.goal && <div className="brief-field"><strong>Goal:</strong> {item.goal}</div>}
          {item.scope && <div className="brief-field"><strong>Scope:</strong> {item.scope}</div>}
          {item.constraints && <div className="brief-field"><strong>Constraints:</strong> {item.constraints}</div>}
          {item.decision && <div className="brief-field"><strong>Decision:</strong> {item.decision}</div>}
          {item.outputFormat && <div className="brief-field"><strong>Format:</strong> {item.outputFormat.replace(/_/g, " ")}</div>}
        </div>
      </div>

      {latest && !err && (() => {
        const res = latest.result || {};
        const summary = typeof res.summary === "string" ? res.summary : res.summary?.bottomLine || res.summary?.summary || null;
        const keyFindings = res.keyFindings || [];
        const recommendations = res.recommendations || [];
        const evidence = res.evidence || [];
        const risks = res.risks || [];
        const unknowns = res.unknowns || [];
        const passes = latest.passes || [];
        const hasParsedResults = summary || keyFindings.length || recommendations.length;

        if (!hasParsedResults) {
          return (
            <div className="detail-results">
              <h3>Research Results (v{item.currentVersion || versions.length})</h3>
              <div className="result-content"><pre>{JSON.stringify(latest, null, 2)}</pre></div>
            </div>
          );
        }

        return (
          <div className="detail-results">
            <h3>Research Results (v{item.currentVersion || versions.length})</h3>

            {summary && (
              <CollapsibleSection title="Summary" defaultOpen={true}>
                <div className="result-content">{summary}</div>
              </CollapsibleSection>
            )}

            {keyFindings.length > 0 && (
              <CollapsibleSection title="Key Findings" count={keyFindings.length}>
                <div className="findings-list">
                  {keyFindings.map((f, i) => (
                    <div key={i} className="finding-item">
                      <div className="finding-text">{typeof f === "string" ? f : f.finding}</div>
                      {f.source && <div className="finding-source">{f.source}</div>}
                      {f.confidence && <span className={`confidence-badge conf-${f.confidence}`}>{f.confidence}</span>}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {recommendations.length > 0 && (
              <CollapsibleSection title="Recommendations" count={recommendations.length}>
                <div className="recommendations-list">
                  {recommendations.map((r, i) => (
                    <div key={i} className="recommendation-item">
                      <div className="recommendation-text">{typeof r === "string" ? r : (r.recommendation || r.action || JSON.stringify(r))}</div>
                      {r.reasoning && <div className="recommendation-reasoning">{r.reasoning}</div>}
                      {r.why && !r.reasoning && <div className="recommendation-reasoning">{r.why}</div>}
                      {r.priority && <span className={`priority-badge priority-${r.priority}`}>{r.priority}</span>}
                      {r.confidence && <span className={`confidence-badge conf-${r.confidence}`}>{r.confidence}</span>}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {evidence.length > 0 && (
              <CollapsibleSection title="Evidence" count={evidence.length}>
                <div className="evidence-list">
                  {evidence.map((e, i) => (
                    <div key={i} className="evidence-item">
                      <div className="evidence-claim">{typeof e === "string" ? e : (e.claim || e.topic || JSON.stringify(e))}</div>
                      {e.source && <div className="evidence-source">{e.source}</div>}
                      {e.url && <a href={e.url} target="_blank" rel="noopener noreferrer" className="evidence-url">{e.url}</a>}
                      {e.confidence && <span className={`confidence-badge conf-${e.confidence}`}>{e.confidence}</span>}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {risks.length > 0 && (
              <CollapsibleSection title="Risks" count={risks.length}>
                <ul className="risks-list">
                  {risks.map((r, i) => (
                    <li key={i}>{typeof r === "string" ? r : (r.risk || JSON.stringify(r))}</li>
                  ))}
                </ul>
              </CollapsibleSection>
            )}

            {unknowns.length > 0 && (
              <CollapsibleSection title="Unknowns" count={unknowns.length}>
                <ul className="unknowns-list">
                  {unknowns.map((u, i) => (
                    <li key={i}>{typeof u === "string" ? u : (u.unknown || JSON.stringify(u))}</li>
                  ))}
                </ul>
              </CollapsibleSection>
            )}

            {passes.length > 0 && (
              <CollapsibleSection title="Research Passes" count={passes.length}>
                <div className="passes-list">
                  {passes.map((p, i) => {
                    const findings = parseFindings(p.findings);
                    return (
                      <CollapsibleSection key={i} title={`Pass ${p.passNumber}: ${p.type?.replace(/_/g, " ")}`}>
                        {findings?.summary?.bottomLine && (
                          <div className="result-content">{findings.summary.bottomLine}</div>
                        )}
                        {findings?.keyFindings && (
                          <ul className="pass-findings">
                            {findings.keyFindings.map((f, j) => (
                              <li key={j}>{typeof f === "string" ? f : f.finding}</li>
                            ))}
                          </ul>
                        )}
                        <div className="pass-meta">{new Date(p.timestamp).toLocaleString()}</div>
                      </CollapsibleSection>
                    );
                  })}
                </div>
              </CollapsibleSection>
            )}

            {item.readableSummary && (
              <div className="annas-review">
                <h4 className="annas-review-title">Anna's Review</h4>
                <div className="annas-review-content">
                  {item.readableSummary.split('\n').map((line, i) => (
                    <p key={i}>{line || '\u00A0'}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="detail-meta">
        <span>Created: {item.createdAt ? new Date(item.createdAt).toLocaleString() : "—"}</span>
        <span>Updated: {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "—"}</span>
        {item.iterations > 0 && <span>Passes: {item.iterations}</span>}
      </div>

      <div className="detail-actions">
        {item.status === "briefing" && (
          <button
            className="btn-primary"
            onClick={handleRetry}
            disabled={retrying}
          >
            {retrying ? "Launching..." : "🚀 Launch Research"}
          </button>
        )}
        {err && (
          <button
            className="btn-primary"
            onClick={handleRetry}
            disabled={retrying}
          >
            {retrying ? "Retrying..." : "🔄 Retry Research"}
          </button>
        )}
        {(item.status === "completed" || item.status === "review") && !err && (
          <button
            className="btn-secondary"
            onClick={handleArchive}
            disabled={archiving}
          >
            {archiving ? "Archiving..." : "Archive"}
          </button>
        )}
      </div>
    </div>
  );
}

function ResearchForm({ onSave, onCancel }) {
  const [form, setForm] = useState({
    title: "",
    goal: "",
    scope: "",
    constraints: "",
    decision: "",
    outputFormat: "detailed_report",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await apiClient.post("/researches", form);
      onSave();
    } catch (err) {
      setError(err.message || "Failed to create research");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="research-form">
      <h3>New Research Brief</h3>
      {error && <div className="form-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g., Best NAS for home server 2025"
            required
          />
        </div>
        <div className="form-group">
          <label>Goal</label>
          <textarea
            value={form.goal}
            onChange={(e) => setForm({ ...form, goal: e.target.value })}
            placeholder="What should Muninn find out?"
            rows={2}
          />
        </div>
        <div className="form-group">
          <label>Scope</label>
          <textarea
            value={form.scope}
            onChange={(e) => setForm({ ...form, scope: e.target.value })}
            placeholder="What to include and exclude..."
            rows={2}
          />
        </div>
        <div className="form-group">
          <label>Constraints</label>
          <textarea
            value={form.constraints}
            onChange={(e) => setForm({ ...form, constraints: e.target.value })}
            placeholder="Budget, timeline, requirements..."
            rows={2}
          />
        </div>
        <div className="form-group">
          <label>Decision</label>
          <input
            value={form.decision}
            onChange={(e) => setForm({ ...form, decision: e.target.value })}
            placeholder="What decision will this research help with?"
          />
        </div>
        <div className="form-group">
          <label>Output Format</label>
          <select
            value={form.outputFormat}
            onChange={(e) => setForm({ ...form, outputFormat: e.target.value })}
          >
            {OUTPUT_FORMATS.map((f) => (
              <option key={f} value={f}>{f.replace(/_/g, " ")}</option>
            ))}
          </select>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Creating..." : "Create Brief"}
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default Researches;
